import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileDown, Mail, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuoteRequestDialog = ({ open, onOpenChange, items, onContactForm }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/quote`, {
        ...form,
        items: items.map(({ service, tier }) => ({ slug: service.slug, tier: tier.name })),
      });
      setResult(data);
      toast.success(data.email_sent ? 'Your PDF quote is on its way to your inbox!' : 'Your PDF quote is ready to download.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create your quote. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setResult(null);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl" style={{ fontFamily: 'var(--font-serif)' }}>
            {result ? 'Quote ready!' : 'Get your PDF quote'}
          </DialogTitle>
          <DialogDescription>
            {result
              ? result.email_sent
                ? `We emailed a branded PDF to ${form.email}. You can also download it below.`
                : 'Download your branded PDF below. Our team has a copy and will follow up shortly.'
              : 'We will email you a branded PDF of this bundle and follow up within one business day.'}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3">
            <Button asChild className="w-full text-white rounded-full" style={{ backgroundColor: '#D97904' }} data-testid="quote-pdf-download">
              <a href={`${BACKEND_URL}${result.pdf_url}`} target="_blank" rel="noopener noreferrer">
                <FileDown className="w-4 h-4 mr-2" /> Download PDF quote
              </a>
            </Button>
            <Button variant="outline" className="w-full rounded-full" onClick={() => onContactForm(form)} data-testid="quote-dialog-contact-form">
              Add more details via contact form
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="quote-request-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="q-name">Full name *</Label>
                <Input id="q-name" name="name" required value={form.name} onChange={update} data-testid="quote-name-input" placeholder="Jane Doe" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="q-email">Email *</Label>
                <Input id="q-email" name="email" type="email" required value={form.email} onChange={update} data-testid="quote-email-input" placeholder="jane@company.com" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="q-phone">Phone</Label>
                <Input id="q-phone" name="phone" value={form.phone} onChange={update} data-testid="quote-phone-input" placeholder="+1 (555) 123-4567" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="q-company">Company</Label>
                <Input id="q-company" name="company" value={form.company} onChange={update} data-testid="quote-company-input" placeholder="Your Company" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full text-white rounded-full py-5" style={{ backgroundColor: '#D97904' }} data-testid="quote-send-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              {loading ? 'Preparing your quote…' : 'Email me the PDF quote'}
            </Button>
            <button type="button" onClick={() => onContactForm(form)} className="w-full text-sm text-gray-500 hover:text-gray-900 underline" data-testid="quote-skip-to-contact">
              Skip, take me to the contact form instead
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteRequestDialog;
